/**
 * Dropbox sign-in: authorisation code + PKCE, and the token store behind it.
 *
 * Two separate problems are fixed here, and it is worth keeping them apart.
 *
 * 1. The popup could not deliver on iOS. MOBILE-IOS.md section 1c: a popup
 *    opened from a home-screen PWA is handed to Safari proper, a different
 *    browsing context with no `window.opener` pointing back into the
 *    standalone one, so postMessage had nobody to talk to and the token was
 *    stranded. Same shape of failure Google had, same shape of fix: on iOS,
 *    navigate the whole page and let Dropbox redirect back.
 *
 * 2. The old flow was implicit (response_type=token), and Dropbox retired
 *    long-lived tokens in 2021. What implicit hands back now is good for four
 *    hours with no way to renew it, which is why signing in to Dropbox felt
 *    like something you had to keep doing. PKCE returns a refresh token
 *    alongside the access token, so a session survives.
 *
 * Unlike Google - whose token endpoint rejects a PKCE exchange that carries no
 * client secret, which is what forced the implicit flow in gisRedirect.ts -
 * Dropbox supports PKCE for public clients properly. There is no secret here
 * and none is needed.
 *
 * The SDK is no help with any of this: dropbox v4's getAuthenticationUrl
 * predates PKCE and only knows how to build an implicit URL. The URL and the
 * exchange are therefore built by hand.
 */

import { shouldUseRedirect } from '../../utilities/platform';

export const appKey = '2sblr267pe2r3uv';

const authEndpoint = 'https://www.dropbox.com/oauth2/authorize';
const tokenEndpoint = 'https://api.dropboxapi.com/oauth2/token';

/**
 * Must match a redirect URI registered in the Dropbox App Console byte for
 * byte. Production is https://fadila.starcraft.is/dropbox-redirect; a dev
 * origin needs registering separately or Dropbox refuses the request.
 */
export const redirectPath = '/dropbox-redirect';

export function redirectUri() {
  return window.location.origin + redirectPath;
}

export interface DropboxIntent {
  /** Make Dropbox the storage provider once a token lands. */
  configureProvider?: boolean;
}

export interface DropboxTokens {
  accessToken: string;
  refreshToken?: string;
  /** Epoch ms. Absent for a token adopted from the old implicit flow. */
  expiresAt?: number;
}

/**
 * The single field the popup posts back to its opener.
 *
 * It carries no token any more. The popup redeems the code itself and writes
 * the token store, which the opener shares with it - same origin - so all the
 * opener needs to hear is that it is worth looking again. That also means a
 * message intercepted by anything else is worthless.
 */
export const popupDoneMessage = 'fadilaDropboxAuthComplete';

const tokensKey = 'dbxAuth';
/** What the implicit flow used to write: a bare access token, no expiry. */
const legacyTokenKey = 'dbxToken';
const pendingKey = 'dbxAuthPending';

/** A round trip that has taken longer than this is not one we still expect. */
const pendingLifetimeMs = 10 * 60 * 1000;

interface PendingAuth {
  state: string;
  verifier: string;
  returnTo: string;
  intent: DropboxIntent;
  createdAt: number;
}

/* -------------------------------------------------------------------------
 * Token store
 * ---------------------------------------------------------------------- */

export function readTokens(): DropboxTokens | undefined {
  try {
    const raw = localStorage.getItem(tokensKey);

    if (raw) {
      const parsed = JSON.parse(raw) as DropboxTokens;
      return parsed && parsed.accessToken ? parsed : undefined;
    }

    // Migrate whatever the implicit flow left behind. It is very probably
    // dead - those tokens lasted four hours - but adopting it costs nothing
    // and a 401 will clear it, which is nicer than logging the user out on
    // sight during an upgrade.
    const legacy = localStorage.getItem(legacyTokenKey);

    if (legacy) {
      const migrated: DropboxTokens = { accessToken: legacy };
      storeTokens(migrated);
      localStorage.removeItem(legacyTokenKey);
      return migrated;
    }

    return undefined;
  }
  catch {
    return undefined;
  }
}

export function storeTokens(tokens: DropboxTokens) {
  try {
    localStorage.setItem(tokensKey, JSON.stringify(tokens));
  }
  catch {
    // Private browsing. The token still works for this page's lifetime.
  }
}

export function clearTokens() {
  try {
    localStorage.removeItem(tokensKey);
    localStorage.removeItem(legacyTokenKey);
  }
  catch { }
}

/* -------------------------------------------------------------------------
 * PKCE
 * ---------------------------------------------------------------------- */

function randomUrlSafe(byteLength: number) {
  const bytes = new Uint8Array(byteLength);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  }
  else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.prototype.map
    .call(bytes, (b: number) => ('0' + b.toString(16)).slice(-2))
    .join('');
}

function base64UrlEncode(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * S256 where SubtleCrypto is available, plain where it is not.
 *
 * crypto.subtle needs a secure context. Everything this app runs in is one -
 * production is HTTPS and the dev server is forced to HTTPS by .env - so the
 * fallback should never fire. It exists so that a browser without it fails by
 * signing in rather than by throwing somewhere unrelated. Dropbox accepts
 * plain, and the verifier never leaves the origin either way.
 */
async function deriveChallenge(verifier: string) {
  try {
    if (window.crypto && window.crypto.subtle) {
      const digest = await window.crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(verifier),
      );

      return { challenge: base64UrlEncode(digest), method: 'S256' };
    }
  }
  catch { }

  return { challenge: verifier, method: 'plain' };
}

/* -------------------------------------------------------------------------
 * Starting the flow
 * ---------------------------------------------------------------------- */

function readPending(): PendingAuth | undefined {
  try {
    const raw = localStorage.getItem(pendingKey);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as PendingAuth;

    if (!parsed || !parsed.state || Date.now() - parsed.createdAt > pendingLifetimeMs) {
      return undefined;
    }

    return parsed;
  }
  catch {
    return undefined;
  }
}

function clearPending() {
  try {
    localStorage.removeItem(pendingKey);
  }
  catch { }
}

/**
 * localStorage rather than sessionStorage, for the same reason gisRedirect
 * gives: iOS may service the cross-origin hop by suspending or even killing
 * the standalone app, and a relaunch starts a fresh session - which would
 * strand the flow at exactly the point it is meant to fix.
 */
function buildAuthUrl(intent?: DropboxIntent) {
  const state = randomUrlSafe(16);
  // 32 bytes hex-encoded is 64 characters, inside Dropbox's 43-128 range.
  const verifier = randomUrlSafe(32);

  try {
    localStorage.setItem(pendingKey, JSON.stringify({
      state,
      verifier,
      // Come back to wherever the user actually was, not a fixed landing page.
      returnTo: window.location.pathname + window.location.search,
      intent: intent || {},
      createdAt: Date.now(),
    } as PendingAuth));
  }
  catch {
    // Private browsing. The trip out still works; the trip back will fail the
    // state check and say so, which beats navigating away silently.
  }

  return deriveChallenge(verifier).then(function buildUrl({ challenge, method }) {
    const params = [
      ['client_id', appKey],
      ['redirect_uri', redirectUri()],
      ['response_type', 'code'],
      ['state', state],
      ['code_challenge', challenge],
      ['code_challenge_method', method],
      // What buys the refresh token, and with it a session that outlives four
      // hours. Without this Dropbox returns an access token and nothing else.
      ['token_access_type', 'offline'],
    ]
      .map(pair => encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]))
      .join('&');

    return authEndpoint + '?' + params;
  });
}

/**
 * Whether this device needs the whole page to go to Dropbox.
 *
 * Exported so callers can tell an authorize() that will return from one that
 * navigates away and never returns at all.
 */
export function willRedirect(allowRedirect?: boolean) {
  return !!allowRedirect && shouldUseRedirect();
}

/**
 * Navigates away. Nothing after this call runs, so the promise a caller is
 * holding never settles - by design, exactly as beginRedirect does for Google.
 */
export async function beginRedirect(intent?: DropboxIntent) {
  const authUrl = await buildAuthUrl(intent);

  // assign, not replace: leaves the app in history so back does something sane.
  window.location.assign(authUrl);
}

/**
 * The desktop path. Returns the popup so the caller can watch it close.
 *
 * The popup is opened on about:blank *synchronously*, before the PKCE
 * challenge is derived, and pointed at Dropbox afterwards. Deriving the
 * challenge is async - crypto.subtle.digest returns a promise - and calling
 * window.open after that await is the very mistake this whole change is
 * about. Opening early and navigating late keeps the call inside the user
 * gesture, where the popup blocker still allows it.
 */
export function beginPopup(intent?: DropboxIntent) {
  const popup = window.open(
    'about:blank',
    'DropboxAuthPopup',
    'width=670,height=800,dialog=yes,dependent=yes,scrollbars=yes,location=yes',
  );

  if (!popup) {
    return Promise.reject(new Error(
      'Dropbox sign-in needs a pop-up window, and this browser blocked it. '
      + 'Allow pop-ups for this site and try again.',
    ));
  }

  return buildAuthUrl(intent).then(
    function pointPopupAtDropbox(authUrl) {
      popup.location.href = authUrl;
      return popup;
    },
    function closePopupOnFailure(err) {
      popup.close();
      throw err;
    },
  );
}

/* -------------------------------------------------------------------------
 * Finishing the flow
 * ---------------------------------------------------------------------- */

export class DropboxAuthError extends Error {
  constructor(message: string, public readonly returnTo: string) {
    super(message);
    this.name = 'DropboxAuthError';
  }
}

async function postForm(body: string[][]) {
  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body
      .map(pair => encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]))
      .join('&'),
  });

  const text = await res.text();
  let parsed: any;

  try {
    parsed = JSON.parse(text);
  }
  catch {
    parsed = undefined;
  }

  if (!res.ok) {
    throw new Error(
      (parsed && (parsed.error_description || parsed.error_summary || parsed.error))
      || 'Dropbox rejected the token request (' + res.status + ').',
    );
  }

  return parsed;
}

function parseParams(str: string) {
  const out: { [key: string]: string } = {};

  str.replace(/^[#?]/, '').split('&').forEach(function readPair(pair) {
    if (!pair) {
      return;
    }

    const idx = pair.indexOf('=');
    const key = decodeURIComponent(idx === -1 ? pair : pair.slice(0, idx));

    out[key] = idx === -1
      ? ''
      : decodeURIComponent(pair.slice(idx + 1).replace(/\+/g, ' '));
  });

  return out;
}

export interface DropboxAuthResult {
  returnTo: string;
  intent: DropboxIntent;
}

/**
 * Exchanges the code Dropbox handed back and stores the tokens.
 *
 * Runs on /dropbox-redirect - which is either the popup or, on iOS, the app
 * itself mid-navigation. Doing the exchange in one place rather than two is
 * why the popup posts a bare "done" to its opener instead of posting a code
 * for the opener to redeem.
 */
export async function completeAuth(search: string): Promise<DropboxAuthResult> {
  const pending = readPending();
  const returnTo = (pending && pending.returnTo) || '/app';

  clearPending();

  const params = parseParams(search);

  if (params.error) {
    throw new DropboxAuthError(
      params.error_description || params.error,
      returnTo,
    );
  }

  if (!pending) {
    throw new DropboxAuthError(
      'Dropbox sign-in could not be completed - the request it answers has expired.',
      returnTo,
    );
  }

  if (!params.state || params.state !== pending.state) {
    throw new DropboxAuthError('Dropbox sign-in returned an unexpected response.', returnTo);
  }

  if (!params.code) {
    throw new DropboxAuthError('Dropbox sign-in returned no authorisation code.', returnTo);
  }

  let payload: any;

  try {
    payload = await postForm([
      ['code', params.code],
      ['grant_type', 'authorization_code'],
      ['client_id', appKey],
      ['redirect_uri', redirectUri()],
      ['code_verifier', pending.verifier],
    ]);
  }
  catch (err) {
    throw new DropboxAuthError((err as Error).message, returnTo);
  }

  if (!payload || !payload.access_token) {
    throw new DropboxAuthError('Dropbox returned no access token.', returnTo);
  }

  storeTokens({
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: payload.expires_in
      ? Date.now() + (payload.expires_in * 1000)
      : undefined,
  });

  const intent = pending.intent || {};

  rememberIntent(intent);

  return { returnTo, intent };
}

/**
 * What the user was trying to achieve by signing in, carried across the trip.
 *
 * On the popup path the caller is still alive and can act on the resolved
 * promise. On the redirect path it is not: /dropbox-redirect runs outside the
 * service tree, and DropboxService only mounts once we navigate back into
 * /app. So the instruction has to outlive the navigation, and something on the
 * other side has to pick it up.
 *
 * localStorage rather than a module variable because iOS may relaunch the app
 * to service the hop, and a relaunch loses module state along with everything
 * else.
 */
const intentKey = 'dbxAuthIntent';

function rememberIntent(intent: DropboxIntent) {
  try {
    if (intent && Object.keys(intent).length) {
      localStorage.setItem(intentKey, JSON.stringify(intent));
    }
  }
  catch { }
}

/** Reads the intent and forgets it. Acting on it twice would be wrong. */
export function takeIntent(): DropboxIntent {
  try {
    const raw = localStorage.getItem(intentKey);

    localStorage.removeItem(intentKey);

    return raw ? JSON.parse(raw) as DropboxIntent : {};
  }
  catch {
    return {};
  }
}

/* -------------------------------------------------------------------------
 * Keeping it alive
 * ---------------------------------------------------------------------- */

/** Renew this far ahead of expiry, so a request in flight does not age out. */
const renewMarginMs = 5 * 60 * 1000;

/**
 * In-flight refresh, shared. The proxy in dropboxService funnels every SDK
 * call through here, and a library scan makes a great many of them at once -
 * without this they would each start their own refresh and race to write the
 * result.
 */
let refreshInFlight: Promise<DropboxTokens> | undefined;

async function refresh(tokens: DropboxTokens): Promise<DropboxTokens> {
  const payload = await postForm([
    ['grant_type', 'refresh_token'],
    ['refresh_token', tokens.refreshToken!],
    ['client_id', appKey],
  ]);

  if (!payload || !payload.access_token) {
    throw new Error('Dropbox returned no access token when renewing.');
  }

  const renewed: DropboxTokens = {
    accessToken: payload.access_token,
    // A refresh response does not repeat the refresh token; keep ours.
    refreshToken: payload.refresh_token || tokens.refreshToken,
    expiresAt: payload.expires_in
      ? Date.now() + (payload.expires_in * 1000)
      : undefined,
  };

  storeTokens(renewed);

  return renewed;
}

/**
 * The current access token, renewed first if it is close to expiring.
 *
 * Returns undefined when there is nothing stored and nothing to renew with -
 * that is "not signed in", not an error.
 */
export function getFreshAccessToken(): Promise<string | undefined> {
  const tokens = readTokens();

  if (!tokens) {
    return Promise.resolve(undefined);
  }

  const stillGood = tokens.expiresAt === undefined
    || tokens.expiresAt - Date.now() > renewMarginMs;

  if (stillGood || !tokens.refreshToken) {
    // No refresh token means a token adopted from the old implicit flow, or a
    // grant made before token_access_type=offline. Hand back what we have and
    // let a 401 sort it out.
    return Promise.resolve(tokens.accessToken);
  }

  if (!refreshInFlight) {
    refreshInFlight = refresh(tokens).finally(function clearInFlight() {
      refreshInFlight = undefined;
    });
  }

  return refreshInFlight.then(
    renewed => renewed.accessToken,
    function useStaleTokenAndLet401Decide() {
      // A failed renewal is not fatal here. Returning the stale token lets the
      // request happen and fail with a 401, which handleDropboxError already
      // turns into "please login again" - one place that decides that, rather
      // than two that disagree.
      return tokens.accessToken;
    },
  );
}
